import { Badge, Group, Paper, Skeleton, Stack, Text, Tooltip } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { useAPIResources } from "@/hooks"
import type { IMetaSurvey, TActivity, TSession, TSurveyQuestion } from "@/types"
import { questionsAPI, surveyInvite } from "@/utils"
import { Markdown } from "../utils"
import { QuestionPreview } from "./questions"

export function SurveyInfo({ activity, session }: { activity: TActivity; session?: TSession }) {
  const { data: { data: questions = [] } = {}, isLoading: isQuestionsLoading } = useAPIResources(questionsAPI, {
    where: { activity_id: activity.id },
  })
  const { invite, reminders } = activity.meta as IMetaSurvey
  const sessionInvite = session?.note || invite
  return (
    <Stack>
      <SurveyInfoMessages invite={sessionInvite} reminders={reminders} />
      <SurveyInfoQuestions questions={questions} isLoading={isQuestionsLoading} />
    </Stack>
  )
}

export function SurveyInfoMessages({ invite, reminders = [] }: { invite?: string; reminders?: string[] }) {
  const { t } = useTranslation()

  return (
    <>
      <Text fz="lg" fw={500}>
        {t("Notification message")}
      </Text>
      <Paper px="xs" mr="md" radius="md" bg="blue" c="white">
        <Markdown>{invite ?? t(surveyInvite)}</Markdown>
      </Paper>
      {reminders.length > 0 && (
        <>
          <Text fz="lg" fw={500}>
            {t("Reminders")}
          </Text>
          {reminders.map((reminder) => (
            <Paper key={reminder} p="xs" mr="md" radius="md" bg="gray" c="white">
              <Markdown>{reminder}</Markdown>
            </Paper>
          ))}
        </>
      )}
    </>
  )
}

export function SurveyInfoQuestions({ questions, isLoading }: { questions: TSurveyQuestion[]; isLoading?: boolean }) {
  const { t } = useTranslation()
  if (isLoading) return <Skeleton height={100} />
  return (
    <>
      <Text fz="lg" fw={500}>
        {t("Questions")}
      </Text>
      {questions.map((question, index) => (
        <SurveyInfoQuestion key={question.id} question={question} idx={index} questions={questions} />
      ))}
    </>
  )
}

export function SurveyInfoQuestion({
  question,
  idx,
  questions,
}: {
  question: TSurveyQuestion
  idx: number
  questions: TSurveyQuestion[]
}) {
  const { t } = useTranslation()
  const branch = question.meta?.branch
  const target = branch && questions.find((q) => q.meta?.ulid === branch.question_ulid)
  const branchLabel = !branch
    ? undefined
    : target?.type === "select"
      ? target.meta?.choices?.[branch.value]
      : branch.value === 0
        ? t("No").toLowerCase()
        : t("Yes").toLowerCase()
  return (
    <Paper p="xs" mr="md" radius="md" withBorder>
      <Group>
        <Tooltip label={t("Required")} disabled={!question.is_required} withArrow>
          <Text fw={600} c={question.is_required ? "red" : "dimmed"}>
            {t("Question")} #{idx + 1}
          </Text>
        </Tooltip>
        {branchLabel && (
          <Text fw={600} c="dimmed">
            ({branchLabel})
          </Text>
        )}
        {question.is_commentable && question.type != "string" && (
          <Tooltip label={t("Commentable")}>
            <Text>💬</Text>
          </Tooltip>
        )}
        {question.category && question.category != "general" && (
          <Badge size="xs" radius="sm" variant="light" tt="uppercase" color="gray">
            {t(question.category)}
          </Badge>
        )}
      </Group>
      <QuestionPreview question={question} />
    </Paper>
  )
}
